import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { useCurrentView } from '@/composables/useCurrentView';
import { useCurrentSegment } from '@/composables/useCurrentSegment';

export function useViewNavigation() {
  const route = useRoute();

  const {
    nextView,
    prevView,

    navigableGroups,
    nextNavigableGroup,
    prevNavigableGroup,
    isCurrentGroupNavigable
  } = useCurrentView();

  const { currentSegment } = useCurrentSegment();

  const getNextViewRoute = () => {
    if (nextView.value) {
      return {
        route: {
          name: 'CalculationViewItems',
          params: {
            ...route.params,
            viewId: nextView.value.id,
          },
        },
        title: 'Dalej',
      };
    }

    if (currentSegment.value?.is_required) {
      return {
        route: {
          name: 'SelectSegmentView',
          params: { ...route.params },
        },
        title: 'Dalej',
      };
    }

    return {
      route: {
        name: 'CalculationSummaryView',
        params: { ...route.params },
      },
      title: 'Podsumowanie',
    };
  };

  const getPrevViewRoute = () => {
    return prevView.value
      ? {
          name: 'CalculationViewItems',
          params: {
            ...route.params,
            viewId: prevView.value.id,
          },
        }
      : {
          name: 'SelectSegmentView',
          params: route.params,
        };
  };

  const isNextStepsNotRequired = computed(() => {
    if (!navigableGroups.value) return false;

    const currentGroupIndex = navigableGroups.value.findIndex(
      (group) => group.id === Number(route.params.groupId)
    );

    const nextGroups = navigableGroups.value.slice(currentGroupIndex + 1);

    if (currentGroupIndex >= navigableGroups.value.length - 1) {
      return false;
    }

    return nextGroups.filter((group) => !group.is_skipable).length === 0;
  });

  const nextNotSkipableGroup = computed(() => {
    if (!navigableGroups.value) return null;

    const currentGroupIndex = navigableGroups.value.findIndex(
      (group) => group.id === Number(route.params.groupId)
    );

    const nextGroups = navigableGroups.value.slice(currentGroupIndex + 1);

    return nextGroups.find((group) => !group.is_skipable) || null;
  });

  const nextElementButton = computed(() => {
    if (isNextStepsNotRequired.value && nextNavigableGroup.value) {
      return {
        route: {
          name: 'CalculationViewItems',
          params: {
            ...route.params,
            viewId: nextNavigableGroup.value.views[0].id,
            groupId: nextNavigableGroup.value.id,
          },
        },
        title: 'Dodaj kolejny element',
      };
    }

    return null;
  });

  const getPrevGroupRoute = () => {
    return prevNavigableGroup.value
      ? {
        name: 'CalculationViewItems',
        params: {
          ...route.params,
          viewId: prevNavigableGroup.value.views[0].id,
          groupId: prevNavigableGroup.value.id,
        },
      }
      : {
        name: 'SelectSegmentView',
        params: route.params,
      };
  };

  const getNextGroupRoute = () => {
    return nextNavigableGroup.value && !isNextStepsNotRequired.value
      ? {
        route: {
          name: 'CalculationViewItems',
          params: {
            ...route.params,
            viewId: nextNavigableGroup.value.views[0].id,
            groupId: nextNavigableGroup.value.id,
          },
        },
        title: 'Dalej',
      }
      : {
        route: {
          name: 'CalculationSummaryView',
          params: { ...route.params },
        },
        title: 'Podsumowanie',
      };
  };

  const nextGroupButton = computed(() => ({
    title: getNextGroupRoute().title,
    route: getNextGroupRoute().route,
  }));

  const prevGroupButton = computed(() => ({
    title: 'Wstecz',
    route: getPrevGroupRoute(),
  }));

  const nextViewButton = computed(() => ({
    title: getNextViewRoute().title,
    route: getNextViewRoute().route,
  }));

  const prevViewButton = computed(() => ({
    title: 'Wstecz',
    route: getPrevViewRoute(),
  }));

  const getNextNotSkipableGroup = () => {
    return nextNavigableGroup.value?.is_skipable &&
    nextNotSkipableGroup.value &&
    nextNotSkipableGroup.value.views[0]
      ? {
        name: 'CalculationViewItems',
        params: {
          ...route.params,
          viewId: nextNotSkipableGroup.value.views[0].id,
          groupId: nextNotSkipableGroup.value.id,
        },
      }
      : null;
  };

  return {
    nextViewButton,
    prevViewButton,
    nextGroupButton,
    prevGroupButton,
    nextElementButton,
    nextNotSkipableGroup,
    getNextNotSkipableGroup,

    isCurrentGroupNavigable,
    navigableGroupsCount: computed(() => navigableGroups.value?.length || 0),
  };
}
